import React, { useMemo, useEffect, useState, useCallback } from 'react';
import './UsersList.scss';

import { useLocation } from 'react-router-dom';
import { ServerResponsiveTable } from '../../../components/ServerResponsiveTable/ServerResponsiveTable';
import { TableColumn, TableAction, TagColor } from '../../../types/responsiveTable';
import { EXTERNAL_USER_STATUSES, ExternalUserTableEntry, USER_CURRENT_ROUTINE_STATUS } from '../../../types/externalUser';
import { mutate } from 'swr';
import { useOrganizationContext } from '../../../context/OrganizationContext';
import { useClient } from '../../../hooks/useClient';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { ExternalUserPanel } from '../../../components/ExternalUserPanel/ExternalUserPanel';
import { Organization } from '../../../types/organization';
import { ALL_FEATURES, Feature, SPECIAL_FEATURES } from '../../../config/features';
import { useAuthContext } from '../../../context/AuthContext';
import { AllowedFeatures } from '../../../types/features';
import { Chip } from '../../../components/Chip/Chip';
import { hasFeaturesPermission } from '../../../utils/featureUtils';
import { ReservationPanel } from '../../../components/ReservationPanel/ReservationPanel';
import { useReservationTypes } from '../../../hooks/AppData/useReservationTypes';
import { ExternalUser } from '../../../types/externalUser';
import { RoutinePanel } from '../../../components/RoutinePanel/RoutinePanel';
import { Routine } from '../../../types/Fisiofit/routine';
import { useNavigateScroll } from '../../../hooks/useNavigateScroll';
import { getCommonRestrictions } from '../../Reservations/Reservations';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { ExportButton } from '../../../components/ExportButton/ExportButton';

const ROUTINE_STATUS_LABELS: Record<USER_CURRENT_ROUTINE_STATUS, string> = {
  [USER_CURRENT_ROUTINE_STATUS.NOT_STARTED]: 'No iniciada',
  [USER_CURRENT_ROUTINE_STATUS.IN_PROGRESS]: 'En curso',
  [USER_CURRENT_ROUTINE_STATUS.EXPIRED]: 'Caducada',
  [USER_CURRENT_ROUTINE_STATUS.NO_ROUTINE]: 'Sin rutina',
  [USER_CURRENT_ROUTINE_STATUS.ENDING_SOON]: 'Acaba pronto',
};

const ROUTINE_STATUS_COLORS: Record<USER_CURRENT_ROUTINE_STATUS, TagColor> = {
  [USER_CURRENT_ROUTINE_STATUS.NOT_STARTED]: 'contrast',
  [USER_CURRENT_ROUTINE_STATUS.IN_PROGRESS]: 'info',
  [USER_CURRENT_ROUTINE_STATUS.EXPIRED]: 'danger',
  [USER_CURRENT_ROUTINE_STATUS.NO_ROUTINE]: 'secondary',
  [USER_CURRENT_ROUTINE_STATUS.ENDING_SOON]: 'warning',
};

const getTableColumns = (
  organization: Organization | undefined,
  features: Feature[] | undefined,
  withVisitTypeSuffix: (label: string) => string,
) => {
  const tableColumns: TableColumn<ExternalUserTableEntry>[] = [
    {
      label: '',
      accessor: 'avatarUrl',
      type: 'avatar',
      sortable: false,
    },
    {
      label: 'Nombre',
      accessor: 'fullName',
      type: 'string',
      isTitle: true,
      valueFromCallBack: (_, row) => [row.name, row.surnames].filter(Boolean).join(' ').trim() || '-',
      renderSuffix: (_, row) => (row.pendingInvitation ? <Chip>Pendiente</Chip> : null),
    },
    {
      label: 'Teléfono',
      accessor: 'phone',
      type: 'string',
      valueFromCallBack: (phone) => phone || '-',
    },
    {
      label: 'Correo electrónico',
      accessor: 'email',
      type: 'string',
      valueFromCallBack: (email) => email || '-',
    },
    {
      label: 'Supervisor',
      accessor: 'supervisor',
      type: 'dropdown',
      valueFromCallBack: (supervisor) => supervisor?.name,
      hideColumn: !organization?.config?.hasSupervisor,
    },
    {
      label: 'Rutina',
      accessor: 'routineStatus',
      type: 'tag',
      textKeyFromCallBack: (routineStatus) => {
        if (!routineStatus) return;
        return ROUTINE_STATUS_LABELS[routineStatus as USER_CURRENT_ROUTINE_STATUS];
      },
      colorFromCallBack: (routineStatus) => {
        if (!routineStatus) return;
        return ROUTINE_STATUS_COLORS[routineStatus as USER_CURRENT_ROUTINE_STATUS];
      },
      hideColumn: !organization?.features.includes(ALL_FEATURES.ROUTINES) || !features?.includes(ALL_FEATURES.ROUTINES),
    },
    {
      label: 'Usuario de gym',
      accessor: 'isGymUser',
      type: 'boolean',
      valueFromCallBack: (isGymUser) => !!isGymUser, // Convertir null/undefined a false
      hideColumn: !organization?.config?.showGymUserField,
    },
    {
      label: withVisitTypeSuffix('Días sin venir'),
      accessor: 'daysSinceLastVisit',
      type: 'number',
      valueFromCallBack: (days) => (days !== null && days !== undefined ? days.toString() : '-'),
    },
    {
      label: 'Estado',
      accessor: 'status',
      textKeyFromCallBack: (status: EXTERNAL_USER_STATUSES) => (status === EXTERNAL_USER_STATUSES.ACTIVE ? 'Activo' : 'Inactivo'),
      colorFromCallBack: (status: EXTERNAL_USER_STATUSES) => {
        return status === EXTERNAL_USER_STATUSES.ACTIVE ? 'success' : 'danger';
      },
      type: 'tag',
    },
    {
      label: 'Fecha de alta',
      accessor: 'registrationDate',
      type: 'calendar',
      valueFromCallBack: (date) => (date ? new Date(date).toLocaleDateString('es-ES') : '-'),
    },
    {
      label: 'Usa app',
      accessor: 'isAppUser',
      type: 'boolean',
      valueFromCallBack: (isAppUser) => !!isAppUser, // Convertir null/undefined a false
    },
    {
      label: 'Sucursal',
      accessor: 'branch',
      type: 'dropdown',
      valueFromCallBack: (branch) => branch?.name,
    },
  ];

  return tableColumns;
};

const UsersList = () => {
  const navigate = useNavigateScroll();
  const { get } = useClient();
  const { organization, organizationLoading } = useOrganizationContext();
  const { features, featuresLoading } = useAuthContext();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const toast = useRef<Toast>(null);

  const userHasEditFeature = hasFeaturesPermission([ALL_FEATURES.EXTERNAL_USERS_WRITE], { features, featuresLoading });

  const [panelVisible, setPanelVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting] = useState(false);

  // Estados para el ReservationPanel
  const [reservationPanelVisible, setReservationPanelVisible] = useState(false);
  const [editingReservation, setEditingReservation] = useState(false);
  const [preselectedExternalUser, setPreselectedExternalUser] = useState<ExternalUser | undefined>();

  // Estados para el RoutinePanel
  const [routinePanelVisible, setRoutinePanelVisible] = useState(false);
  const [selectedRoutine, setSelectedRoutine] = useState<Routine | undefined>();
  const [editingRoutine, setEditingRoutine] = useState(false);

  // Obtener tipos de reserva
  const { reservationTypes, getMainReservationType, withVisitTypeSuffix } = useReservationTypes();
  const mainReservationType = useMemo(() => {
    return getMainReservationType();
  }, [getMainReservationType]);

  const calendarRestrictions = useMemo(() => {
    return getCommonRestrictions(reservationTypes, null, mainReservationType ?? null);
  }, [reservationTypes, mainReservationType]);

  // The prefill comes from the reservation page. The memo depends on the five strings, not on `searchParams`, so a table write
  // to the address bar does not rebuild it and does not run the `openPanel` effect again.
  const prefillName = searchParams.get('name');
  const prefillSurnames = searchParams.get('surnames');
  const prefillPhone = searchParams.get('phone');
  const prefillBranchId = searchParams.get('branchId');
  const prefillReservationId = searchParams.get('reservationId');

  const initialFormData = useMemo(() => {
    if (!prefillName && !prefillSurnames && !prefillPhone) return undefined;

    return {
      name: prefillName ?? undefined,
      surnames: prefillSurnames ?? undefined,
      phone: prefillPhone ?? undefined,
      branchId: prefillBranchId ?? undefined,
      createdFromReservationId: prefillReservationId ?? undefined,
    };
  }, [prefillName, prefillSurnames, prefillPhone, prefillBranchId, prefillReservationId]);

  const openPanel = useCallback((editing: boolean) => {
    setIsEditing(editing);
    setPanelVisible(true);
  }, []);

  const closePanel = useCallback(() => {
    setPanelVisible(false);
  }, []);

  useEffect(() => {
    if (initialFormData) {
      openPanel(false);
    }
  }, [initialFormData, openPanel]);

  // Obtener columnas de la tabla
  const tableColumns = useMemo(() => {
    return getTableColumns(organization, features, withVisitTypeSuffix);
  }, [features, organization, withVisitTypeSuffix]);

  const endpoint = useMemo(() => {
    if (!organization) return '';
    return `/organizations/${organization.id}/external-users/paginated`;
  }, [organization]);

  const options: TableAction[] = useMemo(() => {
    const baseOptions: TableAction[] = [
      {
        label: 'Ver perfil',
        onClick: (id) => {
          navigate(`profile/${id}`);
        },
        hasPermission: hasFeaturesPermission([ALL_FEATURES.EXTERNAL_USERS], { features, featuresLoading }),
      },
      {
        label: 'Crear reserva',
        onClick: async (id: string) => {
          const user = await get<ExternalUser>(`/external-users/${id}`);
          if (user && mainReservationType && organization?.id) {
            setPreselectedExternalUser(user);
            setReservationPanelVisible(true);
          }
        },
        hasPermission: hasFeaturesPermission([SPECIAL_FEATURES.FISIO_RESERVATIONS_WRITE], { features, featuresLoading }),
      },
    ];

    const hasRoutineOption: TableAction = {
      label: 'Ver rutina',
      onClick: async (id: string) => {
        if (!organization?.id) return;
        const user = await get<ExternalUser>(`/organizations/${organization.id}/external-users/${id}`);
        if (!user) return;
        if (!user.currentRoutine?.id) {
          toast.current?.show({
            severity: 'info',
            summary: 'Sin rutina',
            detail: 'Este usuario no tiene ninguna rutina asignada.',
          });
          return;
        }
        const routine = await get<Routine>(`/routines/${user.currentRoutine.id}`);
        if (routine) {
          setSelectedRoutine(routine);
          setRoutinePanelVisible(true);
        }
      },
      hasPermission: hasFeaturesPermission([ALL_FEATURES.ROUTINES], { features, featuresLoading }),
    };

    baseOptions.push(hasRoutineOption);

    return baseOptions;
  }, [features, featuresLoading, mainReservationType, navigate, organization?.id, get]);

  const handleClose = () => {
    closePanel();
    if (initialFormData) {
      // Remove only the prefill params: the table params (filters, sort, page) stay in the address bar.
      const rest = new URLSearchParams(location.search);
      ['name', 'surnames', 'phone', 'branchId', 'reservationId'].forEach((key) => rest.delete(key));
      const search = rest.toString();
      navigate({ pathname: '/users', search: search ? `?${search}` : '' }, { replace: true });
    }
  };

  const allowedFeatures: AllowedFeatures = {
    edit: [ALL_FEATURES.EXTERNAL_USERS_WRITE],
    delete: [ALL_FEATURES.EXTERNAL_USERS_WRITE],
    create: [ALL_FEATURES.EXTERNAL_USERS_WRITE],
  };

  const allowedRoutineFeatures: AllowedFeatures = {
    edit: [ALL_FEATURES.ROUTINES_WRITE],
    delete: [ALL_FEATURES.ROUTINES_WRITE],
    create: [ALL_FEATURES.ROUTINES_WRITE],
  };

  const renderHeaderOptions = useCallback(
    (fetchAllForExport: () => Promise<ExternalUserTableEntry[]>) => (
      <div className='table-header-options'>
        {userHasEditFeature && (
          <Button label='Añadir usuario' outlined rounded icon={PrimeIcons.PLUS} onClick={() => openPanel(false)} />
        )}
        <ExportButton fetchData={fetchAllForExport} columns={tableColumns} fileName='Usuarios' disabled={isDeleting} />
      </div>
    ),
    [userHasEditFeature, openPanel, tableColumns, isDeleting],
  );

  return (
    <>
      <Toast ref={toast} position='top-right' />

      <ServerResponsiveTable
        tableId='users'
        endpoint={endpoint}
        disabled={isDeleting}
        columns={tableColumns}
        options={options}
        headerOptions={renderHeaderOptions}
        onRowClick={(externalUser: ExternalUserTableEntry) => {
          navigate(`profile/${externalUser.id}`);
        }}
      />

      <ExternalUserPanel
        resource={undefined}
        visible={panelVisible}
        onHide={handleClose}
        onIsEditingChange={setIsEditing}
        isEditing={isEditing}
        onDeleteResource={() => Promise.resolve(true)}
        isDeleting={isDeleting}
        initialFormData={initialFormData}
        allowedFeatures={allowedFeatures}
      />

      <ReservationPanel
        visible={reservationPanelVisible}
        onHide={() => {
          setReservationPanelVisible(false);
          setPreselectedExternalUser(undefined);
        }}
        onIsEditingChange={setEditingReservation}
        initialHours={undefined}
        clearInitialTemporaryEvent={() => {}}
        isEditing={editingReservation}
        resource={undefined}
        isDeleting={false}
        createModeReservationTypeId={mainReservationType?.id}
        preselectedExternalUser={preselectedExternalUser}
        mutateReservations={mutate}
        calendarRestrictions={calendarRestrictions}
      />

      <RoutinePanel
        resource={selectedRoutine}
        visible={routinePanelVisible}
        onHide={() => {
          setRoutinePanelVisible(false);
          setSelectedRoutine(undefined);
        }}
        onIsEditingChange={setEditingRoutine}
        isEditing={editingRoutine}
        isDeleting={false}
        allowedFeatures={allowedRoutineFeatures}
      />
    </>
  );
};

export default UsersList;
